import { usePost } from '../../../../api';
import { useCallback, useState } from 'react';
import { USER_MANAGEMENT_BASE_URL } from '../consts';
import { ProcessButtonStatus } from '@silverfort/sf-components';

export interface TestTokenPayload {
    userName: string;
    domainName: string;
    machineName: string;
    token: string;
    tokenType: number;
    device: string;
    providerIds?: string[];
}

interface TestTokenResponse {
    token: TestTokenPayload['token'];
    result: boolean;
    message: string;
}

interface TestTokenData {
    buttonStatus: ProcessButtonStatus;
    message?: string;
}

interface TestTokenState {
    [key: TestTokenPayload['token']]: TestTokenData;
}

const isTestTokenResponse = (state: unknown): state is TestTokenResponse => {
    const assertedState = state as TestTokenResponse;
    return 'token' in assertedState && 'result' in assertedState && 'message' in assertedState;
};

const useTestToken = () => {
    const [tokensStatus, setTokensStatus] = useState<TestTokenState>({});

    const onSuccess = useCallback((response: TestTokenResponse) => {
        const { token, result, message } = response;
        setTokensStatus((prevState) => {
            if (token in prevState) {
                const testResult: TestTokenState = {
                    [token]: {
                        buttonStatus: result ? 'success' : 'failure',
                        message
                    }
                };
                return { ...prevState, ...testResult };
            } else return prevState;
        });
    }, []);

    const onError = useCallback((errorResponse: unknown) => {
        if (isTestTokenResponse(errorResponse)) {
            const { token, message } = errorResponse;
            setTokensStatus((prevState) => {
                if (token in prevState) {
                    const testResult: TestTokenState = {
                        [token]: {
                            buttonStatus: 'failure',
                            message
                        }
                    };
                    return { ...prevState, ...testResult };
                } else {
                    return prevState;
                }
            });
        }
    }, []);

    const getState = useCallback(
        (token: string): TestTokenData => {
            if (token in tokensStatus) {
                return tokensStatus[token];
            }
            return {
                buttonStatus: 'initial'
            };
        },
        [tokensStatus]
    );

    const cancelTest = useCallback((token: string) => {
        setTokensStatus((prevState) => {
            if (token in prevState) {
                const newState = { ...prevState };
                delete newState[token];
                return newState;
            } else return prevState;
        });
    }, []);

    const { post } = usePost<TestTokenPayload, TestTokenResponse>({ onSuccess, onError });

    const testToken = useCallback(async (tokenPayload: TestTokenPayload) => {
        setTokensStatus((prevState) => ({ ...prevState, [tokenPayload.token]: { buttonStatus: 'loading' } }));
        await post(`${USER_MANAGEMENT_BASE_URL}/token/test`, tokenPayload);
    }, []);

    return {
        testToken,
        getState,
        cancelTest
    };
};

export default useTestToken;
